import { useEffect, useState } from 'react'
import { HeadContent, Outlet, Scripts, createRootRouteWithContext } from '@tanstack/react-router'
import type { QueryClient } from '@tanstack/react-query'
import { Toaster } from '#/components/ui/sonner'
import { TooltipProvider } from '#/components/ui/tooltip'
import { getLocale } from '#/paraglide/runtime'
import { m } from '#/paraglide/messages'
import { LoadingState } from '#/components/common/loading-state'
import { NavigationProgress, ConnectionStatus } from '#/components/layout/navigation-progress'
import { SessionCacheBoundary } from '#/components/layout/session-cache-boundary'
import appCss from '../styles.css?url'

interface MyRouterContext {
  queryClient: QueryClient
}

// Anti-flash: apply the stored theme before first paint. Light is the default.
// After a deploy, an open tab may reference JS chunks that no longer exist. Vite fires
// `vite:preloadError` for that; reload once (guarded) instead of crashing the route.
const staleChunkScript = `window.addEventListener('vite:preloadError',function(e){try{e.preventDefault();var k='bystep:reloaded';if(sessionStorage.getItem(k)===location.href){return}sessionStorage.setItem(k,location.href);location.reload()}catch(_){}});`
const errorReportScript = `(function(){function r(m,s){try{fetch('/api/client/error',{method:'POST',headers:{'content-type':'application/json'},body:JSON.stringify({message:String(m).slice(0,2000),stack:String(s||'').slice(0,6000),url:location.href,ua:navigator.userAgent,extra:{source:'window'}})})}catch(e){}}window.addEventListener('error',function(e){if(/ResizeObserver loop/.test(String(e.message)))return;r(e.message,e.error&&e.error.stack)});window.addEventListener('unhandledrejection',function(e){var x=e.reason||{};r(x.message||String(x),x.stack)});})();`
const themeScript = `(function(){try{var t=localStorage.getItem('theme');if(t==='dark'){document.documentElement.classList.add('dark')}}catch(e){}})();`

// ---------- analytics (no-op unless the VITE_* env vars are set — see docs/cli.md "Analytics env")
const GA_ID = import.meta.env.VITE_GA_ID as string | undefined
const CLARITY_ID = import.meta.env.VITE_CLARITY_ID as string | undefined
const ANALYTICS_SRC = import.meta.env.VITE_ANALYTICS_SRC as string | undefined
// First-touch attribution: remember UTM params / referrer of the first landing URL; sent after sign-up (POST /api/auth/attribute-me).
const attributionScript = `(function(){try{var K='bystep.attr';if(localStorage.getItem(K))return;var q=new URLSearchParams(location.search);var s=q.get('utm_source'),md=q.get('utm_medium'),c=q.get('utm_campaign');var r=document.referrer||'';try{if(r&&new URL(r).host===location.host)r=''}catch(e){}if(!s&&!r)return;localStorage.setItem(K,JSON.stringify({source:s||'referral',medium:md||(r?'referral':null),campaign:c||null,referrer:r||null,landingPath:location.pathname+location.search}))}catch(e){}})();`
const analyticsScripts = (): Array<{ src?: string; async?: boolean; children?: string }> => {
  const out: Array<{ src?: string; async?: boolean; children?: string }> = [{ children: attributionScript }]
  if (GA_ID) {
    out.push({ src: `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(GA_ID)}`, async: true })
    out.push({ children: `window.dataLayer=window.dataLayer||[];function gtag(){dataLayer.push(arguments)}gtag('js',new Date());gtag('config','${GA_ID.replace(/[^A-Za-z0-9_-]/g, '')}');` })
  }
  if (CLARITY_ID) out.push({ children: `(function(c,l,a,r,i,t,y){c[a]=c[a]||function(){(c[a].q=c[a].q||[]).push(arguments)};t=l.createElement(r);t.async=1;t.src="https://www.clarity.ms/tag/"+i;y=l.getElementsByTagName(r)[0];y.parentNode.insertBefore(t,y);})(window,document,"clarity","script","${CLARITY_ID.replace(/[^A-Za-z0-9_-]/g, '')}");` })
  if (ANALYTICS_SRC && /^https:\/\//.test(ANALYTICS_SRC)) out.push({ src: ANALYTICS_SRC, async: true })
  return out
}

export const Route = createRootRouteWithContext<MyRouterContext>()({
  head: () => ({
    meta: [
      { charSet: 'utf-8' },
      { name: 'viewport', content: 'width=device-width, initial-scale=1' },
      { title: m.app_name() },
      { name: 'description', content: m.tagline() },
      { name: 'theme-color', content: '#fafaf7' },
      // Orkestrator E5 §4: installable web app (PWA manifest, no service worker — offline is not a goal).
      { name: 'mobile-web-app-capable', content: 'yes' },
      { name: 'apple-mobile-web-app-capable', content: 'yes' },
      { name: 'apple-mobile-web-app-title', content: 'Bystep' },
      { name: 'apple-mobile-web-app-status-bar-style', content: 'default' },
    ],
    links: [
      { rel: 'stylesheet', href: appCss },
      { rel: 'icon', type: 'image/png', sizes: '32x32', href: '/brand/mascot-32.png' },
      { rel: 'icon', type: 'image/png', sizes: '64x64', href: '/brand/mascot-64.png' },
      { rel: 'apple-touch-icon', sizes: '180x180', href: '/brand/mascot-180.png' },
      { rel: 'manifest', href: '/manifest.webmanifest' },
    ],
    scripts: analyticsScripts(),
  }),
  shellComponent: RootDocument,
  component: () => <><SessionCacheBoundary /><NavigationProgress /><ConnectionStatus /><Outlet /></>,
  notFoundComponent: NotFound,
  errorComponent: RootError,
})

function RootDocument({ children }: { children: React.ReactNode }) {
  return (
    <html lang={getLocale()} suppressHydrationWarning>
      <head>
        <HeadContent />
      </head>
      <body>
        <script dangerouslySetInnerHTML={{ __html: themeScript }} />
        <script dangerouslySetInnerHTML={{ __html: staleChunkScript }} />
        <script dangerouslySetInnerHTML={{ __html: errorReportScript }} />
        <TooltipProvider delayDuration={200}>{children}</TooltipProvider>
        <Toaster position="bottom-center" richColors />
        <Scripts />
      </body>
    </html>
  )
}

function RootError({ error: caught }: { error: unknown }) {
  const error = caught instanceof Error ? caught : new Error(String(caught))
  const msg = String(error?.message ?? '')
  const stale = /is not a function|Failed to fetch dynamically imported module|Importing a module script failed|Loading chunk|Unexpected token '<'|error loading dynamically imported module/i.test(msg)
  // Stale-chunk errors (tab opened before a deploy): reload once automatically; the
  // sessionStorage guard prevents a reload loop if the error is a real bug.
  const [reloading, setReloading] = useState(false)
  useEffect(() => {
    try {
      void fetch('/api/client/error', { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ message: msg, stack: String(error?.stack ?? '').slice(0, 6000), url: location.href, ua: navigator.userAgent, extra: { stale, theme: localStorage.getItem('theme'), keys: Object.keys(localStorage) } }) })
    } catch {}
    if (!stale) return
    try {
      const k = 'bystep:reloaded'
      if (sessionStorage.getItem(k) !== location.href) {
        sessionStorage.setItem(k, location.href)
        setReloading(true)
        location.reload()
      }
    } catch {}
  }, [stale])
  if (reloading) return <LoadingState label={m.loading_navigation()} />
  return (
    <div className="flex flex-1 flex-col items-center justify-center gap-3 p-8 text-center">
      <h1 className="text-2xl font-bold">{stale ? m.stale_reload_title() : m.common_error_generic()}</h1>
      <p className="max-w-md text-sm text-muted-foreground">{stale ? m.stale_reload_desc() : msg}</p>
      <div className="flex gap-2">
        <button className="rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground" onClick={() => { try { sessionStorage.removeItem('bystep:reloaded') } catch {}; window.location.reload() }}>
          {m.stale_reload_btn()}
        </button>
        <a href="/" className="rounded-lg border px-4 py-2 text-sm font-medium">{m.err_home()}</a>
      </div>
      <pre className="max-w-2xl overflow-auto whitespace-pre-wrap text-left font-mono text-[11px] text-muted-foreground">{String(error?.stack ?? msg).split('\n').slice(0, 6).join('\n')}</pre>
    </div>
  )
}

function NotFound() {
  return (
    <div className="flex flex-1 flex-col items-center justify-center gap-3 p-8 text-center">
      <h1 className="text-2xl font-bold">{m.err_not_found_title()}</h1>
      <p className="text-muted-foreground">{m.err_not_found_desc()}</p>
      <a href="/" className="text-primary underline-offset-4 hover:underline">
        {m.err_home()}
      </a>
    </div>
  )
}
